import type { ProfileData, ProfilerNodeTiming } from "@recursive-robot/react-jsx-parser";
import type { RootState } from "..";
import type { CycleEntry, ProfilingState } from "./types";

/** A profiler node with its children rebuilt from the flat post-order list. */
export type ProfilerTreeNode = ProfilerNodeTiming & { children: ProfilerTreeNode[] };

/**
 * A human-legible label for a profiler node.
 *
 * `componentName` is only populated on `react` batches; parser `render`/`callback` nodes carry a
 * generic `nodeType` ("JSXElement", "CallExpression", …).  For those we recover the JSX tag name from
 * the node's source (`<Table …>` -> "Table"), or fall back to a short single-line source snippet, so
 * the flame graph is readable without clicking every frame.
 */
export function nodeLabel(node: Pick<ProfilerNodeTiming, "componentName" | "nodeType" | "source">): string {
    if (node.componentName) return node.componentName;

    const src = (node.source ?? "").trim();
    if (src) {
        // JSX element / fragment: use the opening tag name (dotted names like `qerent.Foo` included).
        const tag = src.match(/^<\s*([A-Za-z_$][\w.$]*)/);
        if (tag) return tag[1];

        // Otherwise a compact one-line snippet of the source.
        const oneLine = src.replace(/\s+/g, " ");
        return oneLine.length > 32 ? `${oneLine.slice(0, 32)}…` : oneLine;
    }

    return node.nodeType;
}

export const selectSelectedCycle = (state: RootState): CycleEntry | undefined => {
    const id = state.profiling.selectedCycleId;
    return id ? state.profiling.cyclesById[id] : undefined;
};

/** All batches held by a cycle, in a stable order (render, callbacks, react). */
function cycleBatchList(entry: CycleEntry): ProfileData[] {
    const batches: ProfileData[] = [];
    if (entry.render) batches.push(entry.render);
    batches.push(...entry.callbacks);
    if (entry.react) batches.push(entry.react);
    return batches;
}

/** A render pass: the root Canvas cycle plus all its nested Template cycles (correlated by parentCycleId). */
export type GroupSummary = {
    rootCycleId: string;
    rootCycle: CycleEntry | undefined;
    fileName?: string;
    startTime: number;
    cycleIds: string[];
    cycleCount: number;      // total cycles in the pass (root Canvas + nested Templates)
    templateCount: number;   // nested Template cycles (cycleCount - 1 root)
    callbackCount: number;   // callback batches across the whole pass
    reactCount: number;      // cycles with a react batch
    // Wall-clock span of the whole pass: earliest batch start to latest batch end across every cycle.
    wallTime: number;
    // Σ batch totalTime / node counts across the pass, split by capture side: parser work
    // (render + callback batches) vs React commits (react batches).  The "self" variants count the
    // ROOT cycle's own batches only — nested Template cycles excluded — mirroring a flame frame's
    // self-vs-total; they equal the timeline batch times shown when the pass row is selected.
    // (Σ node.selfTime is NOT a usable self measure here: node self times partition a batch exactly,
    // so that sum just reproduces batch.totalTime.)
    parserTime: number;
    parserSelfTime: number;
    parserNodes: number;
    parserSelfNodes: number;
    reactTime: number;
    reactSelfTime: number;
    reactNodes: number;
    reactSelfNodes: number;
    // Σ totalTime of callback batches only (a subset of parserTime).
    callbackTime: number;
};

function buildGroupSummary(profiling: ProfilingState, rootCycleId: string): GroupSummary {
    const cycleIds = profiling.groupCycleIds[rootCycleId] ?? [];
    const rootCycle = profiling.cyclesById[rootCycleId];

    let min = Infinity, max = -Infinity;
    let callbackCount = 0, reactCount = 0;
    let parserTime = 0, parserSelfTime = 0, parserNodes = 0, parserSelfNodes = 0;
    let reactTime = 0, reactSelfTime = 0, reactNodes = 0, reactSelfNodes = 0, callbackTime = 0;
    for (const id of cycleIds) {
        const entry = profiling.cyclesById[id];
        if (!entry) continue;
        const isRoot = id === rootCycleId;
        callbackCount += entry.callbacks.length;
        if (entry.react) reactCount += 1;
        for (const b of cycleBatchList(entry)) {
            min = Math.min(min, b.startTime);
            max = Math.max(max, b.startTime + b.totalTime);
            if (b.trigger === "react") {
                reactTime += b.totalTime;
                reactNodes += b.nodes.length;
                if (isRoot) { reactSelfTime += b.totalTime; reactSelfNodes += b.nodes.length; }
            } else {
                parserTime += b.totalTime;
                parserNodes += b.nodes.length;
                if (isRoot) { parserSelfTime += b.totalTime; parserSelfNodes += b.nodes.length; }
                if (b.trigger === "callback") callbackTime += b.totalTime;
            }
        }
    }

    return {
        rootCycleId,
        rootCycle,
        fileName: rootCycle?.fileName,
        startTime: rootCycle?.startTime ?? (Number.isFinite(min) ? min : 0),
        cycleIds,
        cycleCount: cycleIds.length,
        templateCount: Math.max(0, cycleIds.length - 1),
        callbackCount,
        reactCount,
        wallTime: Number.isFinite(min) ? max - min : 0,
        parserTime,
        parserSelfTime,
        parserNodes,
        parserSelfNodes,
        reactTime,
        reactSelfTime,
        reactNodes,
        reactSelfNodes,
        callbackTime,
    };
}

/** Retained groups (render passes), newest first — operates on the profiling slice. */
export const groupSummaries = (profiling: ProfilingState): GroupSummary[] =>
    profiling.groupOrder.map(id => buildGroupSummary(profiling, id)).reverse();

/** Retained groups (render passes), newest first (RootState convenience wrapper). */
export const selectGroupList = (state: RootState): GroupSummary[] => groupSummaries(state.profiling);

/**
 * Every batch of every retained cycle that rendered `fileName`, chronological — the timeline's
 * "Template" scope.  Exact equality: inline templates (`undefined`) group together.
 */
export function templateBatches(profiling: ProfilingState, fileName: string | undefined): ProfileData[] {
    return Object.values(profiling.cyclesById)
        .filter(c => c.fileName === fileName)
        .flatMap(cycleBatchList)
        .sort((a, b) => a.startTime - b.startTime);
}

/** A cycle with its child cycles, rebuilt from `parentCycleId` within a group. */
export type CycleTreeNode = { cycle: CycleEntry; children: CycleTreeNode[] };

/**
 * Rebuild the parser tree for a group: the root Canvas cycle at top, nested Template cycles beneath,
 * correlated via `parentCycleId`.  Children are ordered chronologically by their start time.  Orphans
 * (a cycle whose parent isn't in the group — an eviction edge case) attach under the root.
 */
export function cycleTree(profiling: ProfilingState, group: GroupSummary): CycleTreeNode | null {
    const nodes = new Map<string, CycleTreeNode>();
    for (const id of group.cycleIds) {
        const cycle = profiling.cyclesById[id];
        if (cycle) nodes.set(id, { cycle, children: [] });
    }

    const root = nodes.get(group.rootCycleId) ?? null;
    nodes.forEach(node => {
        if (node.cycle.cycleId === group.rootCycleId) return;
        const parentId = node.cycle.parentCycleId;
        const parent = parentId != null ? nodes.get(parentId) : undefined;
        if (parent) parent.children.push(node);
        else if (root) root.children.push(node);
    });

    const sortRec = (n: CycleTreeNode) => {
        n.children.sort((a, b) => a.cycle.startTime - b.cycle.startTime);
        n.children.forEach(sortRec);
    };
    if (root) sortRec(root);
    return root;
}

/**
 * Rebuild the node tree for a batch from `parentId` (the parser emits nodes flat and post-order).
 * Roots have `parentId === null`.
 */
export function toTree(nodes: ProfilerNodeTiming[]): ProfilerTreeNode[] {
    const byId = new Map<number, ProfilerTreeNode>(nodes.map(n => [n.id, { ...n, children: [] }]));
    const roots: ProfilerTreeNode[] = [];
    byId.forEach(n => {
        if (n.parentId === null) {
            roots.push(n);
        } else {
            byId.get(n.parentId)?.children.push(n);
        }
    });
    return roots;
}

/** Nodes of a batch sorted by exclusive (self) time, hottest first. */
export const selectHotNodes = (batch: ProfileData): ProfilerNodeTiming[] =>
    [...batch.nodes].sort((a, b) => b.selfTime - a.selfTime);

export type AggregateRow = {
    key: string;
    label: string;
    fileName?: string;
    source: string;
    line: number;
    totalSelfTime: number;
    totalTime: number;
    count: number;
    avgSelfTime: number;
    // React commit counters — accumulated from `react`-trigger nodes only (React's `<Profiler onRender>`).
    // All zero when "React render timing" is off, since no `react` batches are captured then.
    reactCount: number;        // commits that rendered this node
    totalCommitTime: number;   // Σ actualDuration (react node.totalTime)
    avgCommitTime: number;     // totalCommitTime / reactCount
};

/** Stable identity for a source location across render passes. */
function aggregateKey(fileName: string | undefined, node: ProfilerNodeTiming): string {
    const { startOffset, endOffset, line } = node.location;
    return `${fileName ?? "(inline)"}@${startOffset ?? line}-${endOffset ?? ""}`;
}

/**
 * Aggregate every node across the given cycles (render + callback + react batches), grouped by
 * source location.  Surfaces the consistently-hot spots that individual passes obscure — templates
 * re-render on every parameter change, so a single hot node recurs across many cycles.
 */
export function aggregateCycles(cycles: Iterable<CycleEntry>): AggregateRow[] {
    // `avgSelfTime` / `avgCommitTime` are derived in the final pass, so the accumulator omits them.
    const rows = new Map<string, Omit<AggregateRow, "avgSelfTime" | "avgCommitTime">>();

    const consume = (batch?: ProfileData) => {
        if (!batch) return;
        // Parser counters (count / self / total) accumulate from `render` and `callback` batches only;
        // `react`-trigger batches (the `<Profiler onRender>` pass) feed the commit counters instead.
        // Mixing them would double-count React render time into the parser columns.
        const isReact = batch.trigger === "react";
        for (const node of batch.nodes) {
            // Skip ambient JSXText whitespace between elements — those nodes carry no meaningful source
            // and only add noise to the hotspot table.
            if (!node.source || !node.source.trim()) continue;

            const key = aggregateKey(batch.fileName, node);
            const existing = rows.get(key);
            if (existing) {
                if (isReact) {
                    existing.reactCount += 1;
                    existing.totalCommitTime += node.totalTime;
                } else {
                    existing.totalSelfTime += node.selfTime;
                    existing.totalTime += node.totalTime;
                    existing.count += 1;
                }
            } else {
                rows.set(key, {
                    key,
                    label: nodeLabel(node),
                    fileName: batch.fileName,
                    source: node.source,
                    line: node.location.line,
                    totalSelfTime: isReact ? 0 : node.selfTime,
                    totalTime: isReact ? 0 : node.totalTime,
                    count: isReact ? 0 : 1,
                    reactCount: isReact ? 1 : 0,
                    totalCommitTime: isReact ? node.totalTime : 0,
                });
            }
        }
    };

    for (const entry of cycles) {
        consume(entry.render);
        entry.callbacks.forEach(consume);
        consume(entry.react);
    }

    return Array.from(rows.values())
        .map(r => ({
            ...r,
            avgSelfTime: r.count ? r.totalSelfTime / r.count : 0,
            avgCommitTime: r.reactCount ? r.totalCommitTime / r.reactCount : 0,
        }))
        .sort((a, b) => b.totalSelfTime - a.totalSelfTime);
}

/** All retained cycles aggregated by source location, hottest (Σ self) first. */
export function selectAggregate(state: RootState): AggregateRow[] {
    return aggregateCycles(Object.values(state.profiling.cyclesById));
}
